import { ContentContainer, ContentFull } from "components/layout";
import { EntityLink } from "components/shared/entity-link";
import { Subnav } from "components/shared/subnav";
import { CrmControlledTable } from "components/table/crm-controlled-table";
import { CrmTableColumn } from "components/table/types";
import {
    BridgeEntityResult,
    GetAudienceQuery,
    GetAudienceRecipientsQuery,
    useGetAudienceRecipientsQuery,
} from "generated/graphql";
import React from "react";
import { useLocation } from "react-router-dom";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
import { getProviderFromPath } from "./audience-provider-utils";

interface AudienceMembersProps {
    audience: GetAudienceQuery["getAudience"] | null;
    entities: BridgeEntityResult[];
    subtab: string;
}

export const AudienceMembers: React.FC<AudienceMembersProps> = props => {
    const { audience, entities, subtab } = props;
    const location = useLocation();
    const providerInfo = getProviderFromPath(location.pathname);

    const selectedSubpage = subtab || "recipients";

    const onNavigate = (subpage: string) => {
        if (audience.id) {
            navigate(`${providerInfo.redirectPath}/audiences/${audience.id}/${subpage}`);
        }
    };

    if (!audience) return null;

    const subpages = [
        {
            linkText: "All Recipients",
            subpage: "recipients",
            render: () => <AudienceRecipients entities={entities} audience={audience} />,
        },
    ];

    if (audience.query.queries.length === 0) {
        return null;
    }

    return (
        <div>
            <ContentContainer>
                <Subnav
                    subpages={subpages}
                    selectedSubpage={selectedSubpage}
                    defaultSubpage={subpages[0].subpage}
                    navigate={onNavigate}
                />
            </ContentContainer>
        </div>
    );
};

interface AudienceRecipientsProps {
    audience: GetAudienceQuery["getAudience"];
    entities: BridgeEntityResult[];
}

const AudienceRecipients: React.FC<AudienceRecipientsProps> = props => {
    const { audience, entities } = props;
    const regionId = useRegionId();
    const [page, setPage] = React.useState<number>(1);
    const pageSize = 25;
    const recipientsQuery = useGetAudienceRecipientsQuery(
        {
            input: {
                regionId: regionId!,
                audienceId: audience.id,
                page,
                pageSize,
            },
        },
        {
            keepPreviousData: true,
        },
    );

    const queryResult = recipientsQuery.data?.getAudienceRecipients || null;

    const columns: CrmTableColumn<
        GetAudienceRecipientsQuery["getAudienceRecipients"]["recipients"][0]
    >[] = [
        {
            key: "name",
            header: "Name",
        },
        {
            key: "firstName",
            header: "First name",
        },
        {
            key: "email",
            header: "Email",
        },
        {
            key: "entityType",
            header: "Source",
            renderCell: ({ record }) => {
                const entity = entities.find(e => e.name === record.entityType);
                return <span>{entity?.title ?? record.entityType}</span>;
            },
        },
        {
            key: "entityId",
            header: "",
            renderCell: ({ record }) => {
                return (
                    <EntityLink
                        entityType={record.entityType}
                        entityId={record.entityId}
                    />
                );
            },
        },
    ];

    if (!queryResult) {
        return null;
    }

    return (
        <ContentFull>
            <CrmControlledTable
                columns={columns}
                goToPage={setPage}
                currentPage={page}
                data={recipientsQuery.data.getAudienceRecipients.recipients}
                rowCount={recipientsQuery.data.getAudienceRecipients.total}
                pageSize={pageSize}
                showPaginationWithOnePage={true}
            />
        </ContentFull>
    );
};
